import {useEffect, useMemo, useState, type Dispatch, type SetStateAction} from 'react'

import {clearPendingSsoOrgLogin, PENDING_SSO_ORG_STORAGE_KEY} from './pending-sso-org'
import {DEFAULT_TASK_COMPOSER_SCOPE, type AvailableOrganization, type TaskComposerScope} from './shared'

export interface TaskComposerScopeState {
  selectedScope: TaskComposerScope
  setSelectedScope: Dispatch<SetStateAction<TaskComposerScope>>
  /** Owner preserved from a handoff when it cannot be represented by the picker. */
  launchScopeLogin?: string
  /** Retained owner that still requires SSO before repository-less submission. */
  blockedScopeLogin?: string
  /**
   * The org login an SSO redirect was started for, read once on mount.
   *
   * Callers that own the environment need it too: returning from the handshake
   * has to land back on Sandbox, which is the only place the scope applies.
   */
  pendingSsoOrgLogin: string | null
}

/**
 * Owns which organization a Sandbox draft is scoped to.
 *
 * It lives outside the task composer controller so a surface that switches
 * between destinations can keep one scope: the picker is offered for a Sandbox
 * Chat exactly as it is for a Sandbox task, and moving between them must not
 * silently reset the owner the repository picker filters by.
 */
export function useTaskComposerScope(
  availableOrganizations: AvailableOrganization[],
  initialOrganizationLogin?: string,
): TaskComposerScopeState {
  // After returning from an SSO redirect kicked off by the ScopePicker, restore
  // the scope to the org the user had picked and the Sandbox environment they
  // were on. The sessionStorage read is inlined here (rather than behind a
  // helper) because the linter rejects hidden non-reactive reads during
  // render. The value is only cleared after commit so a StrictMode-abandoned
  // first render can't swallow the one-shot handshake.
  const [pendingSsoOrgLogin] = useState(() => {
    try {
      return sessionStorage.getItem(PENDING_SSO_ORG_STORAGE_KEY)
    } catch {
      return null
    }
  })
  const restoredSsoOrg = useMemo(
    () =>
      pendingSsoOrgLogin
        ? (availableOrganizations.find(org => org.login === pendingSsoOrgLogin && !org.requiresSso) ?? null)
        : null,
    [pendingSsoOrgLogin, availableOrganizations],
  )
  const initialOrganization = useMemo(
    () =>
      initialOrganizationLogin
        ? (availableOrganizations.find(org => org.login.toLowerCase() === initialOrganizationLogin.toLowerCase()) ??
          null)
        : null,
    [availableOrganizations, initialOrganizationLogin],
  )
  const [selectedScope, setSelectedScope] = useState<TaskComposerScope>(() =>
    restoredSsoOrg
      ? {kind: 'organization', organization: restoredSsoOrg}
      : initialOrganization
        ? {kind: 'organization', organization: initialOrganization}
        : DEFAULT_TASK_COMPOSER_SCOPE,
  )
  useEffect(() => {
    if (pendingSsoOrgLogin) clearPendingSsoOrgLogin()
    // Only clear once, after commit — the pending value is a one-shot handshake.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {selectedScope, setSelectedScope, pendingSsoOrgLogin}
}
